import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import api from 'utils/api';

const ServiceModal = ({
  isOpen,
  onClose,
  onSave,
  service = null,
  employees = [],
  selectedSalon,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    duration: 60,
    price: '',
    assignedEmployees: [],
    serviceFeatures: [],
    serviceType: '',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [featureInput, setFeatureInput] = useState('');
  const [selectedType, setSelectedType] = useState('');
  useEffect(() => {
    if (service) {
      setFormData({
        name: service?.name || '',
        description: service?.description || '',
        duration: service?.duration || 60,
        price: service?.price?.toString() || '',
        assignedEmployees: service?.assignedEmployees || [],
        serviceFeatures: service?.serviceFeatures || [],
        serviceType: service?.serviceType || '',
      });
    } else {
      setFormData({
        name: '',
        description: '',
        duration: 60,
        price: '',
        assignedEmployees: [],
        serviceFeatures: [],
        serviceType: '',
      });
    }
    setErrors({});
  }, [service, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'نام خدمت الزامی است';
    }

    // if (!formData?.price || parseFloat(formData?.price) <= 0) {
    //   newErrors.price = 'قیمت باید بیشتر از صفر باشد';
    // }

    if (formData?.duration < 15) {
      newErrors.duration = 'مدت زمان باید حداقل ۱۵ دقیقه باشد';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData?.price),
      };
      console.log('payload', payload);
      let response;
      if (service?.id) {
        // ✅ update service
        response = await api.put(
          `/salons/${selectedSalon}/services/${service.id}`,
          payload
        );
        console.log('response', response);
      } else {
        // ✅ create service
        response = await api.post(`/salons/${selectedSalon}/services`, payload);
      }

      if (onSave) onSave(response.data);

      onClose(); // ✅ close after save
    } catch (error) {
      console.error('Error saving service:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors?.[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleTypeChange = (type) => {
    setSelectedType(type);
  };

  const handleEmployeeToggle = (employeeId) => {
    setFormData((prev) => ({
      ...prev,
      assignedEmployees: prev?.assignedEmployees?.includes(employeeId)
        ? prev?.assignedEmployees?.filter((id) => id !== employeeId)
        : [...prev?.assignedEmployees, employeeId],
    }));
  };

  const getDurationOptions = () => {
    const options = [];
    for (let i = 15; i <= 480; i += 15) {
      const hours = Math.floor(i / 60);
      const minutes = i % 60;
      let label;

      if (hours > 0 && minutes > 0) {
        label = `${hours} ساعت و ${minutes} دقیقه`;
      } else if (hours > 0) {
        label = `${hours} ساعت`;
      } else {
        label = `${minutes} دقیقه`;
      }

      options?.push({ value: i, label });
    }
    return options;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-modal z-modal flex items-center justify-center p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-2xl max-h-[90vh] overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">
            {service ? 'ویرایش خدمت' : 'افزودن خدمت جدید'}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            iconSize={20}
            className="text-muted-foreground hover:text-foreground">
            <span className="sr-only">بستن</span>
          </Button>
        </div>

        {/* Content */}
        <form
          onSubmit={handleSubmit}
          className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="p-6 space-y-6">
            {/* Service Name */}
            <Input
              label="نام خدمت"
              type="text"
              value={formData?.name}
              onChange={(e) => handleInputChange('name', e?.target?.value)}
              placeholder="مثال: کوتاهی مو، رنگ مو، ..."
              error={errors?.name}
              required
            />

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                توضیحات (اختیاری)
              </label>
              <textarea
                value={formData?.description}
                onChange={(e) =>
                  handleInputChange('description', e?.target?.value)
                }
                placeholder="توضیحات کوتاه در مورد خدمت..."
                rows={3}
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              />
            </div>

            {/* Duration and Price */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  مدت زمان <span className="text-error">*</span>
                </label>
                <select
                  value={formData?.duration}
                  onChange={(e) =>
                    handleInputChange('duration', parseInt(e?.target?.value))
                  }
                  className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent">
                  {getDurationOptions()?.map((option) => (
                    <option key={option?.value} value={option?.value}>
                      {option?.label}
                    </option>
                  ))}
                </select>
                {errors?.duration && (
                  <p className="text-error text-sm mt-1">{errors?.duration}</p>
                )}
              </div>

              <Input
                label="قیمت (تومان)"
                type="number"
                value={formData?.price}
                onChange={(e) => handleInputChange('price', e?.target?.value)}
                placeholder="مثال: ۵۰۰۰۰"
                error={errors?.price}
                //  required
                min="0"
                step="1000"
              />
            </div>

            {/* Service Features Section */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                ویژگی‌های خدمت
              </label>

              <div className="flex gap-2 mb-2">
                <Input
                  type="text"
                  placeholder="یک ویژگی وارد کنید..."
                  value={featureInput || ''}
                  onChange={(e) => setFeatureInput(e.target.value)}
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    if (!featureInput) return;
                    setFormData({
                      ...formData,
                      serviceFeatures: [
                        ...formData.serviceFeatures,
                        featureInput,
                      ],
                    });
                    setFeatureInput('');
                  }}>
                  افزودن
                </Button>
              </div>

              {/* Display current features */}
              <div className="space-y-2">
                {formData.serviceFeatures?.map((f, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between p-2 border rounded-md bg-muted">
                    <span>{f}</span>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      iconName="X"
                      onClick={() => {
                        const updated = formData.serviceFeatures.filter(
                          (_, idx) => idx !== i
                        );
                        setFormData({
                          ...formData,
                          serviceFeatures: updated,
                        });
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Employee Assignment */}
            {employees?.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-3">
                  تخصیص به کارکنان
                </label>
                <div className="bg-muted/30 rounded-lg p-4 max-h-48 overflow-y-auto">
                  <div className="space-y-3">
                    {employees?.map((employee) => (
                      <label
                        key={employee?.id}
                        className="flex items-center gap-3 cursor-pointer hover:bg-muted/50 p-2 rounded-md transition-colors">
                        <input
                          type="checkbox"
                          checked={formData?.assignedEmployees?.includes(
                            employee?.id
                          )}
                          onChange={() => handleEmployeeToggle(employee?.id)}
                          className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
                        />
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                            <Icon
                              name="User"
                              size={16}
                              className="text-primary"
                            />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground">
                              {employee?.name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {employee?.role}
                            </p>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {formData?.assignedEmployees?.length} کارمند انتخاب شده
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-muted/30">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}>
              انصراف
            </Button>
            <Button
              type="submit"
              loading={isLoading}
              iconName="Save"
              iconPosition="left">
              {service ? 'ذخیره تغییرات' : 'افزودن خدمت'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServiceModal;
